
import React from 'react';

interface CityRulesProps {
  city: string;
}

type RuleSet = {
  principalResidence: string;
  maxNights: string;
  license: string;
  strata: string;
  taxes: string;
  tip: string;
};

const cityRules: Record<string, RuleSet> = {
  Vancouver: {
    principalResidence:
      'Yes — you must live in the property as your principal residence to host short-term rentals.',
    maxNights:
      '365 nights per year — there is no night cap in Vancouver. You can rent out your entire home year-round as long as you can prove it is your principal residence with utility bills and other documentation.',
    license:
      'You need a Short-Term Rental Business Licence from the City of Vancouver and a Provincial STR Registry number.',
    strata:
      'Your strata corporation must not have bylaws prohibiting short-term rentals. Check your strata minutes and bylaws.',
    taxes:
      'You must collect and remit 8% Provincial Sales Tax (PST), 5% GST (if earning over $30k/year), and 3% Municipal and Regional District Tax (MRDT).',
    tip: 'Vancouver allows unlimited hosting days as long as you can prove principal residence. Keep utility bills, tax documents, and other proof of residency on file.',
  },
  'North Vancouver': {
    principalResidence:
      'Yes — the property must be your principal residence in both the City and District of North Vancouver.',
    maxNights: '365 nights per year. No night cap for principal residence operators.',
    license:
      'A business licence is required from your respective municipality (City or District) plus a Provincial STR Registry number.',
    strata:
      'Strata bylaws may restrict or prohibit STRs. Always verify with your strata council before listing.',
    taxes: 'GST, PST, and MRDT apply. Register for GST if your annual revenue exceeds $30,000.',
    tip: 'The District and City have slightly different rules. Make sure you know which municipality your property falls under.',
  },
  Burnaby: {
    principalResidence: 'Yes — Burnaby requires the property to be your principal residence.',
    maxNights:
      '90 nights per year for entire-home rentals. No cap on private room rentals while you are home.',
    license: 'A Burnaby business licence and Provincial STR Registry number are required.',
    strata:
      'Strata restrictions apply. Many Burnaby stratas have voted to prohibit short-term rentals.',
    taxes: 'Standard BC tax obligations apply: GST, PST, and MRDT.',
    tip: 'Burnaby has a 90-night cap on entire-home rentals — one of the stricter limits in Metro Vancouver.',
  },
  Surrey: {
    principalResidence: 'Yes — Surrey requires principal residence status for STR operators.',
    maxNights:
      '365 nights per year. Surrey does not impose a separate night cap beyond provincial rules.',
    license: 'A Surrey business licence and Provincial STR Registry number are required.',
    strata:
      'Check your strata bylaws. Some Surrey stratas restrict or ban short-term rentals.',
    taxes: 'GST, PST, and MRDT apply as per BC provincial requirements.',
    tip: 'Surrey is an emerging STR market with growing demand, especially near transit hubs and the US border.',
  },
  Langley: {
    principalResidence: 'Yes — both the Township and City of Langley require principal residence.',
    maxNights: '365 nights per year for principal residence operators.',
    license: 'A municipal business licence and Provincial STR Registry number are required.',
    strata: 'Strata bylaws may apply. Verify with your strata council.',
    taxes: 'Standard BC tax obligations: GST, PST, and MRDT.',
    tip: 'The Township of Langley and City of Langley have separate bylaws. Confirm which jurisdiction your property is in.',
  },
  Victoria: {
    principalResidence:
      'Yes — Victoria requires the property to be your principal residence for STR licensing.',
    maxNights: '365 nights per year. No separate night cap for principal residence operators.',
    license: 'A City of Victoria business licence and Provincial STR Registry number are required.',
    strata:
      'Strata restrictions are common in Victoria. Many buildings have voted to prohibit STRs.',
    taxes: 'GST, PST, and MRDT apply. Victoria has active tax enforcement.',
    tip: 'Victoria is a popular tourist destination with strong STR demand, especially during summer months.',
  },
  Whistler: {
    principalResidence:
      'No — Whistler does NOT require principal residence, but your property must be in an approved zoning area.',
    maxNights: '365 nights per year in approved zones.',
    license:
      'A Whistler business licence is required. Zoning approval is critical — only certain zones allow STRs.',
    strata:
      'Many Whistler stratas allow STRs, but always verify. Some have specific rules about minimum stay lengths.',
    taxes: 'GST, PST, MRDT, and an additional Whistler resort tax may apply.',
    tip: 'Whistler is zone‑dependent. Properties outside approved STR zones cannot legally operate regardless of other factors.',
  },
  Calgary: {
    principalResidence:
      'No — Calgary does NOT require you to live in the property. Investors can operate STRs.',
    maxNights: '365 nights per year. No night cap.',
    license: 'A City of Calgary business licence is required. No provincial registry in Alberta.',
    strata: 'Condo bylaws may restrict STRs. Check with your condo board.',
    taxes: 'GST applies (if earning over $30k/year). Alberta has a 4% Tourism Levy.',
    tip: 'Calgary is one of the most investor‑friendly STR markets in Canada with minimal restrictions.',
  },
  Edmonton: {
    principalResidence:
      'No — Edmonton does NOT require principal residence. Investors welcome.',
    maxNights: '365 nights per year. No night cap.',
    license: 'A City of Edmonton business licence is required.',
    strata: 'Condo bylaws may restrict STRs. Always verify with your condo corporation.',
    taxes: 'GST applies (if earning over $30k/year). Alberta Tourism Levy of 4%.',
    tip: 'Edmonton offers excellent flexibility for STR investors with minimal regulatory barriers.',
  },
  Toronto: {
    principalResidence:
      'Yes — Toronto requires the property to be your principal residence.',
    maxNights:
      '180 nights per year for entire-home rentals. No cap on private rooms while you are home.',
    license:
      'Registration with the City of Toronto is required. You must display your registration number on all listings.',
    strata:
      'Condo corporations may have bylaws restricting STRs. Many Toronto condos have voted to ban them.',
    taxes: 'GST (if over $30k/year), HST, and a 4% Municipal Accommodation Tax (MAT) apply.',
    tip: 'Toronto has a strict 180‑night cap on entire‑home rentals and active enforcement with fines up to $100,000.',
  },
};

const defaultRules: RuleSet = {
  principalResidence: 'Requirements vary by municipality. Check your local bylaws.',
  maxNights: 'Night limits vary. Some cities have caps on entire‑home rentals.',
  license: 'Most Canadian cities require a business licence and/or registration for STR operators.',
  strata: 'Strata or condo bylaws may restrict or prohibit short‑term rentals.',
  taxes: 'GST/HST and local accommodation taxes typically apply.',
  tip: 'Contact your local municipality for the most up‑to‑date regulations.',
};

export function CityRules({ city }: CityRulesProps) {
  // Defensive programming – ensure we always have a string
  const safeCity = typeof city === 'string' ? city : '';

  const rules = cityRules[safeCity] || defaultRules;

  const ruleItems = [
    {
      icon: 'ri-home-heart-line',
      title: 'Principal Residence',
      value: rules.principalResidence,
    },
    { icon: 'ri-moon-line', title: 'Maximum Nights', value: rules.maxNights },
    { icon: 'ri-file-list-3-line', title: 'Licensing Required', value: rules.license },
    { icon: 'ri-building-2-line', title: 'Strata / Condo Rules', value: rules.strata },
    { icon: 'ri-money-dollar-circle-line', title: 'Tax Obligations', value: rules.taxes },
  ];

  return (
    <section className="py-12">
      <div className="max-w-3xl mx-auto px-6">
        {/* Use inline style for the custom font to avoid Tailwind parsing issues */}
        <h2
          style={{ fontFamily: "'Fraunces', serif" }}
          className="text-3xl font-bold text-[#505A5A] mb-2"
        >
          {safeCity || 'Your City'} STR Rules at a Glance
        </h2>
        <p className="text-[#505A5A]/60 mb-8 text-sm">
          Key regulations that apply to short‑term rental hosts in {safeCity || 'your area'}.
        </p>

        <div className="space-y-4">
          {ruleItems.map((item, index) => (
            <div
              key={index}
              className="bg-[#F8F8F8] border border-[#E8E8E8] rounded-xl p-5"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#882629]/10 rounded-lg flex-shrink-0">
                  <i className={`${item.icon} text-[#882629] text-xl`}></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-[#505A5A] mb-1">
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#505A5A]/70 leading-relaxed">{item.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pro Tip */}
        <div className="mt-6 bg-[#882629]/5 border-l-4 border-[#882629] rounded-lg p-5">
          <div className="flex items-start gap-3">
            <i className="ri-lightbulb-line text-[#882629] text-xl flex-shrink-0 mt-0.5"></i>
            <div>
              <h4 className="text-sm font-semibold text-[#882629] mb-1">Pro Tip</h4>
              <p className="text-sm text-[#505A5A]/70 leading-relaxed">{rules.tip}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
